import React from 'react';

import { dashboardInvoiceLogApis } from '@apis/next/invoiceLogApis';
import { Layout } from '@components/Layout';
import { PrintableContainer } from '@components/Printable';
import { cellFactory, Table } from '@components/Table/Table';
import { getChangeLogDefaultDetail } from '@data/changeLog';
import { invoiceStrings } from '@data/invoice';
import { useSearch } from '@hooks/next/useSearch';
import { countryStrings } from '@models/Country';
import { InvoiceHelper } from '@models/next/InvoiceHelper';
import { InvoiceLogInvoiceDetails, type InvoiceLogFormatters } from '@models/next/InvoiceLog';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createStrings } from '@utils/local';
import { formatDateTime, formatPrice, toJST } from '@utils/types';
import clsx from 'clsx';
import { max } from 'lodash-es';
import { z } from 'zod';

const useStyles = makeMuiStyles((theme, _common) => ({
  customInner: {
    maxWidth: '65rem',
  },
  background: {
    backgroundColor: theme.palette.background.paper,
  },
  dataNotExist: {
    color: theme.palette.grey[400],
    margin: '3rem auto',
  },
}));

const strings = createStrings({
  ja: {
    title: `${invoiceStrings.changeLog} - ${invoiceStrings.invoice}`,
    dataNotExist: '履歴なし',
    no: 'No',
    updatedAt: '更新時間',
    editor: '編集者',
    editedField: '変更内容',
    beforeValue: '変更前',
    afterValue: '変更後',
    detail: '変更理由',
    unitNumType: '数量・単位',
  },
});

const InvoiceDetailsDisplay: React.FC<{ item: InvoiceLogInvoiceDetails }> = React.memo(
  ({ item }) => {
    const { unitPrice, currency, exciseTax, taxExcludedPrice } =
      InvoiceHelper.getInvoiceDetailsComputed(item);

    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: '0.5rem' }}>
        <div>{invoiceStrings.invoice_name}</div>
        <div>{item.invoice_name}</div>

        <div>{invoiceStrings.invoice_name_preparation}</div>
        <div>{item.invoice_name_preparation || '-'}</div>

        <div>{invoiceStrings.unit_price}</div>
        <div>{unitPrice != null ? formatPrice(unitPrice, currency) : '-'}</div>

        <div>{strings.unitNumType}</div>
        <div>
          {item.unit_num} {item.unit_num_type}
        </div>

        <div>{invoiceStrings.excise_tax}</div>
        <div>{exciseTax != null ? formatPrice(exciseTax, currency) : '-'}</div>

        <div>{invoiceStrings.tax_excluded_price}</div>
        <div>{taxExcludedPrice != null ? formatPrice(taxExcludedPrice, currency) : '-'}</div>
      </div>
    );
  },
);

export const PrintInvoiceLog: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const {
    search: { id },
  } = useSearch(
    z.object({
      id: z.number(),
    }),
  );

  const invoiceLogQuery = dashboardInvoiceLogApis.useGet('/dashboard/invoice-log/:id', {
    params: {
      id,
    },
  });

  const formatters: InvoiceLogFormatters = {
    payer_mark: (v) => countryStrings[v],
  };

  let cellIndex = 0;

  const bodyColumns =
    invoiceLogQuery.data?.flatMap((item) => {
      if (item.edited_field === 'invoice_details') {
        const deleted = item.before_value.filter(
          (v) => !item.after_value.some((after) => after.id === v.id),
        );

        const added = item.after_value.filter(
          (v) => !item.before_value.some((before) => before.id === v.id),
        );

        return [
          ...added.map((invoiceDetails) => [
            cellFactory.create((++cellIndex).toString()),
            cellFactory.create(item.editor || '-'),
            cellFactory.create(formatDateTime(item.updated_at)),
            cellFactory.create(item.label + '・追加' || '-'),
            cellFactory.create('-'),
            cellFactory.create(<InvoiceDetailsDisplay item={invoiceDetails} />),
            cellFactory.create(item.detail || getChangeLogDefaultDetail(item) || '-'),
          ]),
          ...deleted.map((invoiceDetails) => [
            cellFactory.create((++cellIndex).toString()),
            cellFactory.create(item.editor || '-'),
            cellFactory.create(formatDateTime(item.updated_at)),
            cellFactory.create(item.label + '・削除' || '-'),
            cellFactory.create(<InvoiceDetailsDisplay item={invoiceDetails} />),
            cellFactory.create('-'),
            cellFactory.create(item.detail || getChangeLogDefaultDetail(item) || '-'),
          ]),
        ];
      } else {
        // Bug in typescript
        const formatter = (formatters[item.edited_field] ?? String) as typeof String;

        return [
          [
            cellFactory.create((++cellIndex).toString()),
            cellFactory.create(item.editor || '-'),
            cellFactory.create(formatDateTime(item.updated_at)),
            cellFactory.create(item.label || '-'),
            cellFactory.create(formatter(item.before_value) || '-'),
            cellFactory.create(formatter(item.after_value) || '-'),
            cellFactory.create(item.detail || getChangeLogDefaultDetail(item) || '-'),
          ],
        ];
      }
    }) ?? [];

  let contents: React.ReactNode = null;
  const head = (
    <Table.Head>
      <Table.Row
        columns={[
          cellFactory.create(strings.no || '-'),
          cellFactory.create(strings.editor || '-'),
          cellFactory.create(strings.updatedAt || '-'),
          cellFactory.create(strings.editedField || '-'),
          cellFactory.create(strings.beforeValue || '-'),
          cellFactory.create(strings.afterValue || '-'),
          cellFactory.create(strings.detail || '-'),
        ]}
      />
    </Table.Head>
  );

  if (invoiceLogQuery.data && invoiceLogQuery.data?.length > 0) {
    contents = (
      <Table>
        {head}
        <Table.Body>
          {bodyColumns.map((columns) => {
            return <Table.Row columns={columns} />;
          })}
        </Table.Body>
      </Table>
    );
  } else if (invoiceLogQuery.data && invoiceLogQuery.data?.length === 0) {
    contents = (
      <>
        <Table>{head}</Table>
        <p className={classes.dataNotExist}>{strings.dataNotExist}</p>
      </>
    );
  }

  const updatedAt = invoiceLogQuery.data && max(invoiceLogQuery.data.map((d) => d.updated_at));

  return (
    <Layout
      hideHeader
      hideFooter
      hideCookiePolicy
      loading={invoiceLogQuery.isLoading}
      title={strings.title}
      errors={[invoiceLogQuery.error]}
      classes={{
        background: classes.background,
      }}
    >
      <PrintableContainer classes={{ inner: classes.customInner }}>
        {updatedAt && (
          <p className={clsx(classes.horizontalSpace1, classes.middleText)}>
            <span>Latest Update:</span>
            <span>{updatedAt ? toJST(new Date(updatedAt)) : '-'}</span>
          </p>
        )}
        {contents}
      </PrintableContainer>
    </Layout>
  );
};
